import React, { useState, useEffect } from "react";
import { Loader2, XCircle, Play, ChevronRight, CheckCircle, BookOpen, Wallet } from "lucide-react";
import axios from "axios"; // Eklendi

// *** Sabitler ***
const THEME_OPTIONS = ["Aşk", "Sanat", "Günlük Yaşantı"];
const PERIOD_OPTIONS = ["Antik Çağ", "Osmanlı", "Günümüz"];
const STYLE_OPTIONS = ["Belgesel", "Romantik", "Çocuklar İçin"];
const TOTAL_STEPS = 3;

// YÜKLEME SIRASINDA GÖSTERİLECEK BİLGİLER
const LOADING_MESSAGES = [
    "🧠 Yapay zeka, seçtiğiniz tema ve döneme uygun ana hikaye iskeletini oluşturuyor...",
    "⏳ Mekanın tarihçesi ve dönemsel olaylar taranıyor, bilgiler harmanlanıyor...",
    "🎨 'Yapay Zeka Sanatçımız', seçtiğiniz tarza uygun dil tonunu ayarlıyor...",
    "🎙️ Hikaye metni seslendirme motoruna gönderiliyor, saniyelik ses dosyaları birleştiriliyor...",
    "✨ Son dokunuşlar yapılıyor, ses kalitesi ve akıcılık kontrol ediliyor. Az kaldı!"
];

const SelectionButton = ({ children, isSelected, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
            isSelected
                ? "bg-cyan-500 text-gray-900 shadow-lg ring-2 ring-cyan-300"
                : "bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-600"
        }`}
    >
        {children}
    </button>
);

const StoryTextModal = ({ isOpen, onClose, title, text }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center p-4">
            <div className="bg-gray-700 rounded-xl w-full max-w-lg shadow-2xl p-6 space-y-4">
                <div className="flex justify-between items-start border-b border-gray-600 pb-3">
                    <h3 className="text-xl font-bold text-white">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors text-2xl leading-none">
                        &times;
                    </button>
                </div>
                <div className="max-h-96 overflow-y-auto text-gray-300 whitespace-pre-line text-sm leading-relaxed">
                    {text}
                </div>
                <div className="flex justify-end">
                    <button onClick={onClose} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm">
                        Kapat
                    </button>
                </div>
            </div>
        </div>
    );
};

const AudioNarrationCard = ({ placeName, userTokens, onTokenSpent, onShowTokenModal }) => {
    const [isStarted, setIsStarted] = useState(false);
    const [step, setStep] = useState(0); 
    const [selections, setSelections] = useState({ theme: null, period: null, style: null });
    const [currentMsgIndex, setCurrentMsgIndex] = useState(0);
    const [audioStatus, setAudioStatus] = useState("idle"); 
    const [audioUrl, setAudioUrl] = useState(null);
    const [storyText, setStoryText] = useState(null); 
    const [isModalOpen, setIsModalOpen] = useState(false); 
    
    // 🚨 YENİ: Dinamik Maliyet State'i
    const [audioCost, setAudioCost] = useState(null); 
    
    const API_URL = process.env.REACT_APP_API_URL;
    
    // 🚨 YENİ: Component yüklendiğinde maliyeti DB'den çeken useEffect
    useEffect(() => {
        const fetchCost = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/features/costs`);
                // Backend'deki key: "audio_narration" (Yoksa fallback olarak 10 al)
                setAudioCost(response.data.audio_narration || 10); 
            } catch (error) {
                console.error("Maliyet bilgisi çekilemedi, varsayılan (10) uygulanıyor.", error);
                setAudioCost(10);
            }
        };
        fetchCost();
    }, [API_URL]);

    // Loading Messages Animation
    useEffect(() => {
        let interval;
        if (audioStatus === "loading") {
            interval = setInterval(() => {
                setCurrentMsgIndex(prevIndex => (prevIndex + 1) % LOADING_MESSAGES.length);
            }, 4000); 
        } else {
            clearInterval(interval);
            setCurrentMsgIndex(0);
        }
        return () => clearInterval(interval);
    }, [audioStatus]);

    const handleSelection = (key, value) => setSelections(prev => ({ ...prev, [key]: value }));
    const handleNext = () => setStep(step < TOTAL_STEPS ? step + 1 : step);
    const handleBack = () => setStep(step > 1 ? step - 1 : step);
    
    const handleRestart = () => {
        setIsStarted(false); setStep(0); setAudioStatus("idle"); 
        setAudioUrl(null); setStoryText(null); 
        setSelections({ theme: null, period: null, style: null });
    };

    const handleGenerateAudio = async () => {
        // Eğer cost henüz sunucudan gelmediyse işlemi bekle
        if (audioCost === null) return; 

        // 🚨 1. Bakiye Kontrolü (Dinamik Cost ile)
        if (userTokens !== null && userTokens < audioCost) {
            if (onShowTokenModal) {
                onShowTokenModal(`Sesli hikaye oluşturmak için yeterli tokeniniz bulunmuyor.`, audioCost);
            } else {
                alert(`Yetersiz Bakiye! Bu işlem için ${audioCost} token gerekli.`);
            }
            return;
        }

        setAudioStatus("loading");
        
        // 🚨 2. Arayüzde Tokeni Optimistik Olarak Düş
        if (onTokenSpent) onTokenSpent(audioCost);

        const { theme, period, style } = selections;
        const apiUrl = `${API_URL}/api/gcloud-voice/${placeName}/${theme}/${period}/${style}`;
    
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}` 
                }
            });
    
            if (!response.ok) {
                // 🚨 3. Hata alırsak tokeni geri iade et
                if (onTokenSpent) onTokenSpent(-audioCost); 
                
                if (response.status === 402) {
                    if (onShowTokenModal) onShowTokenModal('Yetersiz bakiye tespit edildi. Lütfen yükleme yapın.', audioCost);
                    setAudioStatus("idle"); 
                    return;
                }
                
                const errorData = await response.json();
                throw new Error(errorData.detail || "Sunucu Hatası: Ses/Metin alınamadı.");
            }
    
            const data = await response.json(); 
            setStoryText(data.story_text); 
            
            const audioBlob = await fetch(`data:${data.mime_type};base64,${data.audio_base64}`).then(res => res.blob());
            const url = URL.createObjectURL(audioBlob);
            
            setAudioUrl(url);
            setAudioStatus("success");
            
        } catch (error) {
            console.error("Hikaye oluşturulamadı:", error);
            if (error.message !== "Yetersiz bakiye tespit edildi. Lütfen yükleme yapın."){
                if (onTokenSpent) onTokenSpent(-audioCost); 
            }
            setAudioStatus("error");
        }
    };
    
    const renderStartScreen = () => (
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0 sm:space-x-4">
            <p className="text-sm text-gray-400 sm:text-left text-center flex-grow">
                Bu mekânın ilginç hikayesini ve bilinmeyenlerini yapay zeka
                sesimizden dinleyin. 
                {/* Yükleniyorsa boş bırak, yüklendiyse dinamik fiyatı göster */}
                {audioCost !== null && (
                    <span className="text-cyan-400 font-semibold ml-1">(Maliyet: {audioCost} Token)</span>
                )}
            </p>
            <button
                type="button"
                onClick={() => { setIsStarted(true); setStep(1); }}
                className="flex items-center gap-2 px-5 py-2 font-semibold rounded-full text-sm text-gray-900 bg-cyan-400 hover:bg-cyan-300 transition-colors shadow-md transform hover:scale-[1.05] whitespace-nowrap"
            >
                <Play className="h-4 w-4" />
                Hikayeyi Kişiselleştir & Başla
            </button>
        </div>
    );
    
    const renderSelectionStep = () => {
        let title = ""; let options = []; let key = ""; let isLastStep = false;
        switch (step) {
            case 1: title = "1/3 Hikaye Anlatım Teması Seçimi"; options = THEME_OPTIONS; key = "theme"; break;
            case 2: title = "2/3 Hikaye Anlatım Dönemi Seçimi"; options = PERIOD_OPTIONS; key = "period"; break;
            case 3: title = "3/3 Hikaye Anlatım Tarzı Seçimi"; options = STYLE_OPTIONS; key = "style"; isLastStep = true; break;
            default: return null;
        }
        const selectedValue = selections[key];
        const isCurrentSelectionMade = selectedValue !== null;

        return (
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h4 className="text-base font-semibold text-white">{title}</h4>
                    <div className="flex items-center gap-1 text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded-md">
                        <Wallet size={12} className="text-cyan-400"/> {userTokens !== null ? userTokens : 0} Token
                    </div>
                </div>
                
                <div className="flex flex-wrap gap-2 text-center align-center justify-center">
                    {options.map((option) => (
                        <SelectionButton key={option} isSelected={selectedValue === option} onClick={() => handleSelection(key, option)}>
                            {option}
                        </SelectionButton>
                    ))}
                </div>
                
                <div className="flex justify-between border-t border-gray-600 pt-3 mt-4">
                    <button type="button" onClick={handleBack} disabled={step === 1} className="text-sm text-gray-400 hover:text-white disabled:opacity-50 transition-colors">&lt; Geri</button>
                    {isLastStep ? (
                        <button type="button" onClick={handleGenerateAudio} disabled={!isCurrentSelectionMade || audioCost === null} className="flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-lg text-gray-900 bg-green-500 hover:bg-green-400 disabled:bg-gray-600 disabled:text-gray-400 transition-colors shadow-lg shadow-green-500/30">
                            <CheckCircle className="h-4 w-4" /> Oluştur {audioCost !== null ? `(${audioCost} Token)` : '...'}
                        </button>
                    ) : (
                        <button type="button" onClick={handleNext} disabled={!isCurrentSelectionMade} className="flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-lg text-gray-900 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 disabled:text-gray-400 transition-colors">
                            İleri <ChevronRight className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderLoadingScreen = () => {
        const { theme, period, style } = selections;
        return (
            <div className="space-y-4 py-3 text-center">
                <h4 className="text-base font-semibold text-white">Seçimlerinizle Hikaye Oluşturuluyor...</h4>
                <div className="flex justify-center flex-wrap gap-2 text-sm">
                    <span className="px-3 py-1 bg-cyan-600/30 text-cyan-200 rounded-full font-medium">{theme}</span>
                    <span className="px-3 py-1 bg-cyan-600/30 text-cyan-200 rounded-full font-medium">{period}</span>
                    <span className="px-3 py-1 bg-cyan-600/30 text-cyan-200 rounded-full font-medium">{style}</span>
                </div>
                <div className="flex flex-col items-center justify-center pt-4">
                    <Loader2 className="h-8 w-8 text-cyan-400 animate-spin mb-4" />
                    <div className="min-h-[4rem] flex items-center justify-center p-3 bg-gray-800 rounded-lg w-full">
                        <span key={currentMsgIndex} className="text-sm font-medium text-gray-300 animate-fade-in-out">
                            {LOADING_MESSAGES[currentMsgIndex]}
                        </span>
                    </div>
                    <span className="text-xs text-gray-500 mt-2">Bu işlem yaklaşık 30 saniye sürebilir. Lütfen bekleyin.</span>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-gray-700 p-4 -mb-2 rounded-xl shadow-2xl border border-gray-700 space-y-3 transition-all duration-300 transform hover:scale-[1.01] hover:shadow-cyan-500/10">
            <h3 className="text-lg font-bold text-cyan-400 flex items-center space-x-2 border-b border-gray-600 pb-2">
                <span role="img" aria-label="speaker">🔊</span>
                <span>Mekânname Sesli Hikaye</span>
            </h3>

            {audioStatus === "idle" && !isStarted && renderStartScreen()}
            {audioStatus === "idle" && isStarted && renderSelectionStep()}
            {audioStatus === "loading" && renderLoadingScreen()}
            
            {audioStatus === "success" && audioUrl && (
                <div className="space-y-3">
                    <div className="p-3 bg-gray-800 rounded-lg shadow-inner">
                        <p className="text-sm font-medium text-white mb-2">Hikayeniz Hazır:</p>
                        <audio controls src={audioUrl} className="w-full rounded-full">
                            Tarayıcınız ses çaları desteklemiyor.
                        </audio>
                    </div>
                    
                    <div className="flex justify-between items-center pt-1">
                        {storyText && (
                            <button type="button" onClick={() => setIsModalOpen(true)} className="flex items-center text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium">
                                <BookOpen className="h-4 w-4 mr-1" />
                                Hikaye Metnini Oku
                            </button>
                        )}
                        <button type="button" onClick={handleRestart} className="text-sm text-gray-400 hover:text-white transition-colors ml-auto border border-gray-600 px-3 py-1 rounded-full">
                            Başka Hikaye Oluştur
                        </button>
                    </div>
                </div>
            )}
            
            {audioStatus === "error" && (
                <div className="flex items-center space-x-3 p-3 bg-red-900/30 rounded-lg border border-red-900/50">
                    <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <p className="text-red-400 text-sm font-medium flex-grow">Sesli anlatım alınamadı. Lütfen tekrar deneyin.</p>
                    <button type="button" onClick={handleRestart} className="text-sm text-red-400 hover:text-red-300 border border-red-400 px-3 py-1 rounded-full transition-colors whitespace-nowrap">
                        Yeniden Başla
                    </button>
                </div>
            )}
            
            <StoryTextModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={`'${selections.theme}' Temalı Hikaye Metni`} text={storyText} />
        </div>
    );
};

export default AudioNarrationCard;